import { calculateSettlementDate } from "@goodfill/business-days";
import { OrderTicketDetailEntity } from "@goodfill/domain-entities";
import {
  calculateGoodfillTransactionCommissionForAmount,
  calculatePriceFromYield,
  calculateYield,
  excludeGoodfillCommissionFromLoadedPrice,
  GoodfillTransactionCommission,
  includeGoodfillCommissionWithBasePrice,
} from "@goodfill/domain-math";
import {
  OrderTicketId,
  TradeTicketId,
  Amount,
  BondPriceNominal,
  Yield,
  asAmount,
  asYield,
  OrderDirection,
  TimeInForce,
  Money,
  Currency,
} from "@goodfill/domain-properties";
import { resolveTimeInForce } from "@goodfill/models-typescript";
import Decimal from "decimal.js";
import { DateTime } from "luxon";
import { PricedBond } from "../../../bonds/PricedBond";
import { createExpirationDate } from "./createExpirationDate";
import { TIMEZONE_NEY_YORK } from "../../../../locale/timezone";

const DEFAULT_FACE_AMOUNT = asAmount(new Decimal("10000"));

export type PricedBondForCreateOrder = Pick<
  PricedBond,
  | "bondIdCusip"
  | "accruedInterestRate"
  | "couponPayment"
  | "maturityDate"
  | "minimumPiece"
  | "couponFrequency"
  | "askPrice"
  | "bidPrice"
>;

/**
 * Data that are "arguments" of the Order Process, those will never change.
 */
interface OrderFormStateInputValues {
  orderId: OrderTicketId | null;
  orderDirection: OrderDirection;
  availableToSell: Amount | null; // only required for Sell
  pricedBond: PricedBondForCreateOrder;
  tradeDate: DateTime;
  settlementDate: DateTime;
  closingTradeTicketId: TradeTicketId | null;
}

/**
 * Data from the form inputs. This can be changed by the user.
 */
export interface OrderFormStateValues {
  faceAmount: Money;
  isAllOrNothing: boolean;
  expirationDate: DateTime | null;
  priceVsYield: "price" | "yield";
  timeInForce: TimeInForce;
  customLoadedPrice: BondPriceNominal;
  customLoadedYield: Yield;
}

/**
 * Calculated data that are recalculated every time something changes in the form!
 */
interface OrderFormStateCalculatedValues {
  /** This is the price, that is based on Bonds price (bid or ask) */
  defaultLoadedPrice: BondPriceNominal;
  /** This is the yield, that is based on Bonds price (bid or ask) */
  defaultLoadedYield: Yield;
  /** The amount of Accrued Interest to be paid from buyer to seller */
  accruedInterest: Amount;
  /** Percentage size of the transaction costs */
  transactionCostRateUpfront: Decimal;
  /** Absolute transaction cost costs */
  transactionCostAmount: Amount;
}

function _calculateLoadedPriceAndYield(
  basePrice: BondPriceNominal,
  faceAmount: Money,
  direction: OrderDirection,
  bond: PricedBondForCreateOrder,
  commission: GoodfillTransactionCommission,
  settlementDate: DateTime,
) {
  const loadedPrice = includeGoodfillCommissionWithBasePrice(basePrice, faceAmount, direction);

  const loadedYield = commission.commissionRate.isFinite()
    ? calculateYield(
        TIMEZONE_NEY_YORK,
        bond.couponPayment,
        bond.couponFrequency,
        loadedPrice,
        bond.maturityDate,
        settlementDate,
      )
    : asYield(new Decimal(0));

  return { loadedPrice, loadedYield };
}

function _calculate(faceAmount: Money, input: OrderFormStateInputValues): OrderFormStateCalculatedValues {
  const pricedBond = input.pricedBond;

  const commission = calculateGoodfillTransactionCommissionForAmount(faceAmount);
  const accruedInterest = asAmount(pricedBond.accruedInterestRate.mul(faceAmount.amount));
  const defaultBasePrice = input.orderDirection === OrderDirection.Buy ? pricedBond.askPrice : pricedBond.bidPrice;

  const { loadedPrice, loadedYield } = _calculateLoadedPriceAndYield(
    defaultBasePrice,
    faceAmount,
    input.orderDirection,
    pricedBond,
    commission,
    input.settlementDate,
  );

  return {
    accruedInterest,
    transactionCostRateUpfront: commission.commissionRate,
    transactionCostAmount: commission.commissionAmount.amount,
    defaultLoadedPrice: loadedPrice,
    defaultLoadedYield: loadedYield,
  };
}

export class OrderFormState {
  _state: OrderFormStateValues;
  _calculated: OrderFormStateCalculatedValues;
  _input: OrderFormStateInputValues;
  _edit: boolean;

  private constructor(
    state: OrderFormStateValues,
    calculated: OrderFormStateCalculatedValues,
    input: OrderFormStateInputValues,
    edit: boolean,
  ) {
    if (input.orderDirection === OrderDirection.Sell && input.availableToSell === null) {
      throw new Error("Sell has to have availableToSell");
    }

    Object.freeze(state);
    Object.freeze(calculated);
    this._state = state;
    this._calculated = calculated;
    this._input = input;
    this._edit = edit;
  }

  static createDefault(
    orderDirection: OrderDirection,
    pricedBond: PricedBondForCreateOrder,
    tradeDate: DateTime,
    closingTradeTicketId: TradeTicketId | null,
    availableToSell: Amount | null,
  ): OrderFormState {
    const faceAmount: Money = {
      amount: availableToSell ?? DEFAULT_FACE_AMOUNT,
      currency: Currency.USD,
    };

    const settlementDate = calculateSettlementDate(tradeDate, TIMEZONE_NEY_YORK);

    const input: OrderFormStateInputValues = {
      orderId: null,
      orderDirection,
      pricedBond,
      tradeDate,
      settlementDate,
      closingTradeTicketId,
      availableToSell,
    };

    const calculated = _calculate(faceAmount, input);

    return new OrderFormState(
      {
        faceAmount,
        expirationDate: null,
        priceVsYield: "yield",
        timeInForce: TimeInForce.Immediate,
        isAllOrNothing: true,
        customLoadedPrice: calculated.defaultLoadedPrice,
        customLoadedYield: calculated.defaultLoadedYield,
      },
      calculated,
      input,
      false,
    );
  }

  static createFromOrder(order: OrderTicketDetailEntity, pricedBond: PricedBond, tradeDate: DateTime): OrderFormState {
    const faceAmount: Money = {
      amount: order.faceAmount,
      currency: Currency.USD,
    };

    const settlementDate = calculateSettlementDate(tradeDate, TIMEZONE_NEY_YORK);

    const input: OrderFormStateInputValues = {
      orderId: order.id,
      orderDirection: order.direction,
      pricedBond,
      tradeDate,
      settlementDate,
      closingTradeTicketId: order.closingTradeTicketId,
      availableToSell: null,
    };

    const calculated = _calculate(faceAmount, input);

    const customLoadedPrice = includeGoodfillCommissionWithBasePrice(
      order.bondPrice,
      faceAmount,
      input.orderDirection,
    );
    const customLoadedYield = calculateYield(
      TIMEZONE_NEY_YORK,
      pricedBond.couponPayment,
      pricedBond.couponFrequency,
      customLoadedPrice,
      pricedBond.maturityDate,
      settlementDate,
    );

    const timeInForce = resolveTimeInForce(order);

    return new OrderFormState(
      {
        faceAmount,
        expirationDate: order.expiresAt,
        priceVsYield: "price",
        timeInForce,
        isAllOrNothing: order.faceAmount.equals(order.minimumFillSize),
        customLoadedPrice,
        customLoadedYield,
      },
      calculated,
      input,
      true,
    );
  }

  isEdit() {
    return this._edit;
  }

  // By passing the expirationDate we can avoid being it null in the state
  getYieldOnExpirationDate(expirationDate: DateTime): Yield {
    const bond = this._input.pricedBond;

    const expirationSettlementDate = calculateSettlementDate(expirationDate, TIMEZONE_NEY_YORK);

    return calculateYield(
      TIMEZONE_NEY_YORK,
      bond.couponPayment,
      bond.couponFrequency,
      this._state.customLoadedPrice,
      bond.maturityDate,
      expirationSettlementDate,
    );
  }

  getBasePrice() {
    if (
      this.s.timeInForce === TimeInForce.Immediate &&
      // Todo: remove this `isEdit()` once backend properly handles the TimeInForce
      !this.isEdit()
    ) {
      return this.i.orderDirection === OrderDirection.Buy ? this.i.pricedBond.askPrice : this.i.pricedBond.bidPrice;
    }

    return excludeGoodfillCommissionFromLoadedPrice(
      this.s.customLoadedPrice,
      this.s.faceAmount,
      this.i.orderDirection,
    );
  }

  resetToMarketPrice() {
    return new OrderFormState(
      {
        ...this._state,
        customLoadedPrice: this._calculated.defaultLoadedPrice,
        customLoadedYield: this._calculated.defaultLoadedYield,
      },
      this._calculated,
      this._input,
      this._edit,
    ).withImmediate();
  }

  withFaceAmount(amount: Amount) {
    const faceAmount = { amount, currency: this._state.faceAmount.currency };
    const calculated = _calculate(faceAmount, this._input);

    const commission = calculateGoodfillTransactionCommissionForAmount(faceAmount);

    const { loadedPrice, loadedYield } = _calculateLoadedPriceAndYield(
      this.getBasePrice(),
      faceAmount,
      this.i.orderDirection,
      this.i.pricedBond,
      commission,
      this.i.settlementDate,
    );

    return new OrderFormState(
      {
        ...this._state,
        faceAmount,
        customLoadedPrice: loadedPrice,
        customLoadedYield: loadedYield,
      },
      calculated,
      this._input,
      this._edit,
    );
  }

  withCustomLoadedPrice(newLoadedPrice: BondPriceNominal, currentTime: DateTime) {
    return new OrderFormState(
      {
        ...this._state,
        customLoadedPrice: newLoadedPrice,
        customLoadedYield: calculateYield(
          TIMEZONE_NEY_YORK,
          this._input.pricedBond.couponPayment,
          this._input.pricedBond.couponFrequency,
          newLoadedPrice,
          this._input.pricedBond.maturityDate,
          this._input.settlementDate,
        ),
      },
      this._calculated,
      this._input,
      this._edit,
    ).withCustomFuture(currentTime);
  }

  withCustomLoadedYield(newLoadedYield: Yield, currentTime: DateTime) {
    return new OrderFormState(
      {
        ...this._state,
        customLoadedYield: newLoadedYield,
        customLoadedPrice: calculatePriceFromYield(
          TIMEZONE_NEY_YORK,
          this._input.pricedBond.couponPayment,
          this._input.pricedBond.couponFrequency,
          newLoadedYield,
          this._input.pricedBond.maturityDate,
          this._input.settlementDate,
        ),
      },
      this._calculated,
      this._input,
      this._edit,
    ).withCustomFuture(currentTime);
  }

  withPriceVsYield(value: "price" | "yield") {
    return new OrderFormState({ ...this._state, priceVsYield: value }, this._calculated, this._input, this._edit);
  }

  withExpirationDate(value: DateTime) {
    return new OrderFormState({ ...this._state, expirationDate: value }, this._calculated, this._input, this._edit);
  }

  withTimeInForce(timeInForce: TimeInForce, currentTime: DateTime): OrderFormState {
    if (timeInForce === TimeInForce.Immediate) {
      return this.withImmediate();
    }

    if (timeInForce === TimeInForce.CustomFuture) {
      return this.withCustomFuture(currentTime);
    }

    throw new Error(`Unsupported ${timeInForce}`);
  }

  private withCustomFuture(currentTime: DateTime) {
    const expirationDate =
      this._state.expirationDate === null
        ? createExpirationDate(TIMEZONE_NEY_YORK, currentTime, TimeInForce.CustomFuture)
        : this._state.expirationDate;

    console.log("expirationDate", expirationDate.toISO());

    return new OrderFormState(
      {
        ...this._state,
        timeInForce: TimeInForce.CustomFuture,
        isAllOrNothing: false,
        expirationDate,
      },
      this._calculated,
      this._input,
      this._edit,
    );
  }

  private withImmediate() {
    return new OrderFormState(
      {
        ...this._state,
        timeInForce: TimeInForce.Immediate,
        isAllOrNothing: true,
        expirationDate: null,
      },
      this._calculated,
      this._input,
      this._edit,
    );
  }

  withIsAllOrNothing(isAllOrNothing: boolean) {
    return new OrderFormState(
      {
        ...this._state,
        isAllOrNothing,
      },
      this._calculated,
      this._input,
      this._edit,
    );
  }

  get s(): OrderFormStateValues {
    return this._state;
  }

  get c(): OrderFormStateCalculatedValues {
    return this._calculated;
  }

  get i(): OrderFormStateInputValues {
    return this._input;
  }

  hasNonMarketPriceOrYield(): boolean {
    return (
      !this._state.customLoadedPrice.equals(this._calculated.defaultLoadedPrice) ||
      !this._state.customLoadedYield.equals(this._calculated.defaultLoadedYield)
    );
  }

  isImmediate() {
    return this.s.timeInForce === TimeInForce.Immediate;
  }

  isCustomFuture() {
    return this.s.timeInForce === TimeInForce.CustomFuture;
  }

  isBuy() {
    return this.i.orderDirection === OrderDirection.Buy;
  }

  isSell() {
    return this.i.orderDirection === OrderDirection.Sell;
  }

  get settlementAmount(): Amount {
    return asAmount(this.s.faceAmount.amount.mul(this.s.customLoadedPrice).plus(this.c.accruedInterest));
  }
}
